import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Consistent modal for the app.
 * - Mobile: bottom-sheet (rounded top only), full width, safe-area padding
 * - Desktop: centered card with max-width
 * - Internal scroll: header/footer stay fixed, body scrolls
 * - Rendered via portal to document.body so `position:fixed` escapes any transformed
 *   ancestor (e.g. `<main class="animate-fade-in">` which retains transform:matrix(...)).
 */
export default function Modal({ open, onClose, title, children, footer, size = "lg", testId = "modal" }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;
  const maxW = size === "sm" ? "sm:max-w-sm" : size === "md" ? "sm:max-w-md" : size === "xl" ? "sm:max-w-xl" : "sm:max-w-lg";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className={`bg-white w-full ${maxW} rounded-t-3xl sm:rounded-3xl flex flex-col shadow-2xl overflow-hidden max-h-[90dvh] sm:max-h-[85vh]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-bizma-border shrink-0">
          <h3 className="font-display font-semibold text-lg">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Tutup"
            className="w-9 h-9 rounded-full hover:bg-bizma-muted grid place-items-center text-bizma-subtext transition-colors"
            data-testid={`${testId}-close`}
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-bizma-border shrink-0 flex gap-3 bg-white">{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
}
